/**
 * @author: chenlv
 * @date: 2025/01/22
 * @description: MetricDataGames 游戏列表组件
 */
import React, { useMemo, useState, useImperativeHandle } from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { history } from 'umi';
import { Segment } from 'tea-component';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import Loading from '@intelligence/components/Loading';
import NoData from '@intelligence/components/NoData';
import EllipsisHover from '@home/components/EllipsisHover';
import TipContext from '@home/components/TipContextComponents/TipContext';
import GrowthRate, { formatGrowthRate } from '@intelligence/v3/components/TableComponents/GrowthRate';
import ARightClickToJump from '@intelligence/components/ARightClickToJump';
import { getDetailGameOverviewPath } from '@intelligence/utils/topchartsUtils';
import { fetchCasualGenreProfileTopGameMetrics, fetchCasualGenreProfileRisingGameMetrics } from '../../services';
import { getCountryFlag } from '@home/_config/countries';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';
import { generateMetricKey, generateMetricRatioKey } from '@intelligence/v3/utils/metric';
import { buildMetricCompareData } from '@intelligence/pages/TopCharts/_config/TableCloums/metricCompare';
import { METRIC_NAME_I18N_KEY_MAP, getGameDataSourceMetric } from '../../constants';

import type { SegmentOption } from 'tea-component/lib/segment/SegmentOption';
import type { IDataCompareData, IDataCompareHelper } from '@intelligence/v3/components/DataCompareBubble';
import type { IGenreModuleInstance, IGenreModuleUpdateParams, TDataSourceType, TMetricType } from '../../types';
import type { TGamesTabType } from './types';

import './index.scss';

/**
 * MetricDataGames 组件 Props
 */
interface IMetricDataGamesProps extends IBaseProps {
  /** 是否折叠（隐藏）游戏列表 */
  collapsed?: boolean;
  /** 当前选中数据源 */
  dataSource: TDataSourceType;
  /** 当前选中指标 */
  metric: TMetricType;
  /** 当前筛选器参数 */
  filterParams: any;
}

/**
 * MetricDataGames 游戏列表组件
 * @description 展示游戏数据列表，支持Top Games和Rising Games两个Tab切换
 */
const MetricDataGames: React.ForwardRefRenderFunction<IGenreModuleInstance, IMetricDataGamesProps> = (props, ref) => {
  const { style, className, collapsed = false, dataSource, metric, filterParams } = props;

  const { locale, formatMessage } = useIntlExtend();

  /**
   * 获取当前指标对应的国际化key
   */
  const currentMetricI18nKey = useMemo<string>(() => {
    return METRIC_NAME_I18N_KEY_MAP[metric];
  }, [metric]);

  const segmentOptions: SegmentOption[] = [
    { value: 'top', text: formatMessage({ id: 'dbi_casual_top_games' }) },
    { value: 'rising', text: formatMessage({ id: 'dbi_casual_rising_games' }) },
  ];

  /**
   * 获取当前粒度
   */
  const granularity = filterParams?.granularity || 'monthly';

  /**
   * 根据粒度获取提示文案（不区分指标）
   */
  const risingGamesTip = useMemo(() => {
    const tipMap: IRecord = {
      // 日粒度提示
      daily: (
        <>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_daily_download' })}</div>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_daily_dau' })}</div>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_daily_revenue' })}</div>
        </>
      ),
      // 月粒度提示
      monthly: (
        <>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_monthly_download' })}</div>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_monthly_mau' })}</div>
          <div>{formatMessage({ id: 'dbi_casual_rising_games_tip_monthly_revenue' })}</div>
        </>
      ),
    };

    return tipMap[granularity] || '';
  }, [locale, granularity]);

  /**
   * 游戏列表Tab状态
   */
  const [tab, setTab] = useState<TGamesTabType>('top');
  const [loading, setLoading] = useState(true);
  const [topGames, setTopGames] = useState<ICasualGenreProfileMetricData[]>([]);
  const [risingGames, setRisingGames] = useState<ICasualGenreProfileMetricData[]>([]);

  /**
   * 获取当前显示的游戏列表
   */
  const currentGames = useMemo<ICasualGenreProfileMetricData[]>(() => {
    const gamesMap: Record<TGamesTabType, ICasualGenreProfileMetricData[]> = {
      top: topGames,
      rising: risingGames,
    };
    return gamesMap[tab] || [];
  }, [tab, topGames, risingGames]);

  const noData = useMemo<boolean>(() => {
    return isEmpty(currentGames);
  }, [currentGames]);

  /**
   * 处理Tab切换
   */
  const handleTabChange = (payload: TGamesTabType): void => {
    setTab(payload);
    const updateParams: IGenreModuleUpdateParams = {
      genreSource: '',
      dataSource,
      metric,
      filterParams,
    };
    void update(updateParams, payload);
  };

  const getGameDetailPath = (gameId?: string): string => {
    if (!gameId) return '';
    return getDetailGameOverviewPath(gameId, 'mobile');
  };

  const handleGameClick = (game?: ICasualGenreProfileMetricData | null): void => {
    const url = getGameDetailPath(game?.game_id);
    if (url) {
      history.push(url);
    }
  };

  const formatCompareDate = (date?: any): string => {
    if (!date) {
      return '';
    }

    const dateMoment = moment(date);

    if (!dateMoment.isValid()) {
      return '';
    }

    return dateMoment.format('YYYY-MM-DD');
  };

  const getGrowthRateCompareData = (
    game: ICasualGenreProfileMetricData | null | undefined,
    metricKey: string,
    metricRatioKey: string,
  ): (IDataCompareData & IDataCompareHelper) | undefined => {
    const rangeTimeValue = filterParams?.[filterKeyMap.RANGE_TIME]?.value || [];
    const startDate = filterParams?.start_date || filterParams?.startDate || rangeTimeValue?.[0];
    const endDate = filterParams?.end_date || filterParams?.endDate || rangeTimeValue?.[1];

    return buildMetricCompareData({
      metric: game,
      granularity,
      startDate: formatCompareDate(startDate),
      endDate: formatCompareDate(endDate),
      title: game?.name || '',
      icon: game?.cover || '',
      entityType: 'mobile',
      valueKey: metricKey,
      prevValueConfig: {
        prevValueKeys: [`${metricKey}_prev`],
        ratioValueKeys: [metricRatioKey],
      },
      valueFormatter: formatKMBWithPlaceholder,
    });
  };

  const update = async (params: IGenreModuleUpdateParams, tab: TGamesTabType) => {
    const { dataSource: newDataSource, metric: newMetric, filterParams } = params;
    setLoading(true);
    try {
      switch (tab) {
        case 'top': {
          const res = await fetchCasualGenreProfileTopGameMetrics(newDataSource, newMetric, filterParams);
          setTopGames(res?.data?.list || []);
          break;
        }
        case 'rising': {
          const res = await fetchCasualGenreProfileRisingGameMetrics(newDataSource, newMetric, filterParams);
          setRisingGames(res?.data?.list || []);
          break;
        }
        default: {
        }
      }
    } catch (error) {
      console.error('fetch games error:', error);
      setTopGames([]);
      setRisingGames([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * 渲染游戏列表项
   */
  const renderGameItem = (game?: ICasualGenreProfileMetricData | null): React.ReactNode => {
    const gameId = game?.game_id || '';
    const gameName = game?.name || '';
    const detailPath = getGameDetailPath(gameId);
    let metricKey = generateMetricKey({ dataSource, metric });
    metricKey = getGameDataSourceMetric(metricKey);
    const metricRatioKey = generateMetricRatioKey(metricKey);
    const metricValue = game?.[metricKey as keyof ICasualGenreProfileMetricData] as number;
    const metricRatioValue = game?.[metricRatioKey as keyof ICasualGenreProfileMetricData] as number;
    const growthRate = formatGrowthRate(metricRatioValue);
    const dataCompareData = getGrowthRateCompareData(game, metricKey, metricRatioKey);

    return (
      <ARightClickToJump
        key={gameId}
        href={detailPath}
        className="dbi-metric-data-games__item-link"
      >
        <div
          className="dbi-metric-data-games__item"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            handleGameClick(game);
          }}
        >
          {/* 左侧：游戏图标 + 游戏信息 */}
          <div className="dbi-metric-data-games__item-left">
            <img className="dbi-metric-data-games__game-icon" src={game?.cover} alt="" />
            <div className="dbi-metric-data-games__game-info">
              <div className="dbi-metric-data-games__game-name">
                <EllipsisHover text={gameName} />
              </div>
              <div className="dbi-metric-data-games__publisher">
                <img
                  className="dbi-metric-data-games__country-flag"
                  src={getCountryFlag(game?.publisher_addr || '')}
                  alt=""
                />
                <div className="dbi-metric-data-games__publisher-name">
                  <EllipsisHover text={game?.publisher || ''} />
                </div>
              </div>
            </div>
          </div>

          {/* 右侧：指标数值 + 增长率 */}
          <div className="dbi-metric-data-games__item-right">
            <div className="dbi-metric-data-games__metric-value">{formatKMBWithPlaceholder(metricValue)}</div>
            <GrowthRate growthRate={growthRate} dataCompareData={dataCompareData} />
          </div>
        </div>
      </ARightClickToJump>
    );
  };

  /**
   * 渲染加载状态
   */
  const renderLoading = (): React.ReactNode => {
    return (
      <div className="dbi-metric-data-games__loading">
        <Loading />
      </div>
    );
  };

  /**
   * 渲染空状态
   */
  const renderEmpty = (): React.ReactNode => {
    return (
      <div className="dbi-metric-data-games__no-data">
        <NoData content={formatMessage({ id: 'common_nodata' })} />
      </div>
    );
  };

  useImperativeHandle(ref, () => ({
    update: async (params: IGenreModuleUpdateParams) => {
      void update(params, tab);
    },
  }));

  return (
    <div
      style={style}
      className={classnames('dbi-metric-data-games', className, {
        'dbi-metric-data-games--collapsed': collapsed,
      })}
    >
      {/* Tab切换 */}
      <div className="dbi-metric-data-games__segment-row">
        <Segment options={segmentOptions} value={tab} onChange={handleTabChange} />
        <div className="dbi-metric-data-games__segment-tip">
          <TipContext arrowPointAtCenter AWidth={450} placement="top-end" tipText={risingGamesTip} />
        </div>
      </div>
      {/* 表头 */}
      <div className="dbi-metric-data-games__header">
        <div className="dbi-metric-data-games__header-title dbi-metric-data-games__header-title--game">
          {formatMessage({ id: 'dbi_casual_game' })}
        </div>
        <div className="dbi-metric-data-games__header-title dbi-metric-data-games__header-title--download">
          {formatMessage({ id: currentMetricI18nKey })}
        </div>
      </div>
      <div className="dbi-metric-data-games__header-line" />
      {/* 加载态 */}
      {loading && renderLoading()}
      {/* 无数据态 */}
      {!loading && noData && renderEmpty()}
      {/* 游戏列表 */}
      {!loading && !noData && <div className="dbi-metric-data-games__list">{currentGames.map(renderGameItem)}</div>}
    </div>
  );
};

// @ts-ignore
export default React.forwardRef(MetricDataGames);
